import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Calendar, Phone, Clock, CheckCircle, Trash2, Plus, X, Users, Search, ChevronDown } from 'lucide-react';
import { apiService, ScheduledEvent, CreateEventDTO, Customer } from '../services/api';

const typeIcons: Record<ScheduledEvent['type'], React.ReactNode> = {
    meeting: <Calendar size={18} />,
    call: <Phone size={18} />,
    follow_up: <Clock size={18} />,
    task: <CheckCircle size={18} />,
};

const statusColors: Record<ScheduledEvent['status'], string> = {
    pending: 'var(--blue)',
    reminded: 'var(--warning)',
    completed: 'var(--success)',
    missed: 'var(--error)',
};

export const EventsPage: React.FC = () => {
    const [events, setEvents] = useState<ScheduledEvent[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [filter, setFilter] = useState<{ status?: string; type?: string }>({});

    const [newEvent, setNewEvent] = useState<CreateEventDTO>({
        type: 'meeting',
        title: '',
        scheduledAt: '',
        description: '',
    });

    // Customer search state
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const customerDropdownRef = useRef<HTMLDivElement>(null);

    const filteredCustomers = customers.filter(c => {
        const searchLower = customerSearch.toLowerCase();
        return (
            (c.name && c.name.toLowerCase().includes(searchLower)) ||
            (c.whatsappName && c.whatsappName.toLowerCase().includes(searchLower)) ||
            (c.phoneNumber && c.phoneNumber.includes(customerSearch))
        );
    });

    const loadEvents = useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiService.getEvents({
                status: filter.status,
                type: filter.type,
                limit: 100,
            });
            setEvents(result.events);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    const loadCustomers = async () => {
        try {
            const data = await apiService.getCustomers();
            setCustomers(data);
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    useEffect(() => {
        loadEvents();
        loadCustomers();
    }, [loadEvents]);

    const handleCreateEvent = async () => {
        if (!newEvent.title || !newEvent.scheduledAt) {
            alert('Title and scheduled date are required');
            return;
        }

        try {
            await apiService.createEvent(newEvent);
            setShowModal(false);
            setNewEvent({ type: 'meeting', title: '', scheduledAt: '', description: '' });
            loadEvents();
        } catch (error) {
            console.error('Error creating event:', error);
        }
    };

    const handleCompleteEvent = async (id: string) => {
        try {
            await apiService.completeEvent(id);
            setEvents(prev => prev.map(e => e._id === id ? { ...e, status: 'completed' as const } : e));
        } catch (error) {
            console.error('Error completing event:', error);
        }
    };

    const handleDeleteEvent = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;

        try {
            await apiService.deleteEvent(id);
            setEvents(prev => prev.filter(e => e._id !== id));
        } catch (error) {
            console.error('Error deleting event:', error);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    };

    const isOverdue = (event: ScheduledEvent) => {
        return event.status === 'pending' && new Date(event.scheduledAt) < new Date();
    };

    return (
        <div className="page-grid">
            <section className="panel span-3">
                <header className="panel-header">
                    <div>
                        <p className="eyebrow">Scheduled Events</p>
                        <h2>
                            <Calendar size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                            Events Calendar
                        </h2>
                    </div>
                    <button className="button-primary" onClick={() => setShowModal(true)}>
                        <Plus size={16} /> New Event
                    </button>
                </header>

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <button
                        className={`pill ${!filter.status ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, status: undefined })}
                    >
                        All
                    </button>
                    <button
                        className={`pill ${filter.status === 'pending' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, status: 'pending' })}
                    >
                        Pending
                    </button>
                    <button
                        className={`pill ${filter.status === 'completed' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, status: 'completed' })}
                    >
                        Completed
                    </button>
                    <button
                        className={`pill ${filter.status === 'missed' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, status: 'missed' })}
                    >
                        Missed
                    </button>
                    <div style={{ width: '1px', background: 'var(--border)', margin: '0 0.5rem' }} />
                    <button
                        className={`pill ${!filter.type ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: undefined })}
                    >
                        All Types
                    </button>
                    <button
                        className={`pill ${filter.type === 'meeting' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: 'meeting' })}
                    >
                        Meetings
                    </button>
                    <button
                        className={`pill ${filter.type === 'call' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: 'call' })}
                    >
                        Calls
                    </button>
                    <button
                        className={`pill ${filter.type === 'follow_up' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: 'follow_up' })}
                    >
                        Follow-ups
                    </button>
                </div>

                {loading ? (
                    <div className="loading-panel">
                        <div className="spinner"></div>
                        <p>Loading events...</p>
                    </div>
                ) : events.length === 0 ? (
                    <div className="empty-state">
                        <Calendar size={48} />
                        <p>No scheduled events found.</p>
                    </div>
                ) : (
                    <div className="events-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {events.map((event) => (
                            <article
                                key={event._id}
                                className="event-card"
                                style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    padding: '1rem',
                                    background: event.status === 'completed' ? 'var(--surface-muted)' : 'var(--surface)',
                                    borderRadius: 'var(--radius-md)',
                                    borderLeft: `4px solid ${statusColors[event.status]}`,
                                    opacity: event.status === 'completed' ? 0.7 : 1,
                                }}
                            >
                                <div style={{ color: statusColors[event.status] }}>
                                    {typeIcons[event.type]}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <h3 style={{ margin: 0, fontSize: '1rem' }}>{event.title}</h3>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            {event.status === 'pending' && (
                                                <button className="ghost-button" onClick={() => handleCompleteEvent(event._id)}>
                                                    <CheckCircle size={16} />
                                                </button>
                                            )}
                                            <button className="ghost-button" style={{ color: 'var(--error)' }} onClick={() => handleDeleteEvent(event._id)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '1rem', margin: '0.5rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                        <span style={{ color: isOverdue(event) ? 'var(--error)' : undefined }}>
                                            <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
                                            {formatDate(event.scheduledAt)}
                                            {isOverdue(event) && ' (Overdue)'}
                                        </span>
                                        {event.customerId && (
                                            <span>
                                                <Users size={14} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
                                                {event.customerId.name || event.customerId.whatsappName || 'Customer'}
                                            </span>
                                        )}
                                    </div>
                                    {event.description && (
                                        <p style={{ margin: '0.5rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                            {event.description}
                                        </p>
                                    )}
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                        <span className={`pill ${event.status === 'completed' ? 'success' : event.status === 'missed' ? 'error' : 'muted'}`}>
                                            {event.status}
                                        </span>
                                        <span className="pill muted">{event.type}</span>
                                        <span className="pill muted" style={{ fontSize: '0.7rem' }}>
                                            by {event.createdBy}
                                        </span>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            {/* Create Event Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '90%',
                            maxWidth: '640px',
                            background: 'var(--surface)',
                            borderRadius: 'var(--radius-lg)',
                            overflow: 'hidden',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        }}
                    >
                        {/* Modal Header with Gradient */}
                        <header style={{
                            background: 'linear-gradient(135deg, var(--accent) 0%, var(--brand-purple) 100%)',
                            padding: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: 'var(--radius-md)',
                                    background: 'rgba(255,255,255,0.2)',
                                    display: 'grid',
                                    placeItems: 'center',
                                }}>
                                    <Calendar size={22} color="white" />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, color: 'white', fontSize: '1.25rem' }}>Create New Event</h2>
                                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Schedule a meeting, call, or follow-up</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.15)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    padding: '0.5rem',
                                    cursor: 'pointer',
                                    color: 'white',
                                    display: 'flex',
                                }}
                            >
                                <X size={20} />
                            </button>
                        </header>

                        {/* Modal Body */}
                        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            {/* Event Type Selection */}
                            <div className="form-group">
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.5rem',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: 'var(--text-secondary)',
                                }}>
                                    <Calendar size={14} />
                                    Event Type
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                                    {(['meeting', 'call', 'follow_up', 'task'] as const).map((type) => (
                                        <button
                                            key={type}
                                            onClick={() => setNewEvent({ ...newEvent, type })}
                                            style={{
                                                padding: '0.75rem 0.5rem',
                                                border: newEvent.type === type ? '2px solid var(--accent)' : '1px solid var(--border)',
                                                borderRadius: 'var(--radius-md)',
                                                background: newEvent.type === type ? 'rgba(29, 78, 216, 0.08)' : 'var(--surface-muted)',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0.35rem',
                                                transition: 'all 0.2s ease',
                                                color: newEvent.type === type ? 'var(--accent)' : 'var(--text-secondary)',
                                            }}
                                        >
                                            {typeIcons[type]}
                                            <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize' }}>
                                                {type.replace('_', '-')}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Title Input */}
                            <div className="form-group">
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: 'var(--text-secondary)',
                                }}>
                                    Title <span style={{ color: 'var(--error)' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newEvent.title}
                                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                                    placeholder="e.g., Project kickoff meeting"
                                    style={{
                                        width: '100%',
                                        padding: '0.85rem 1rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border)',
                                        background: 'var(--surface-muted)',
                                        fontSize: '0.95rem',
                                        transition: 'border-color 0.2s, box-shadow 0.2s',
                                        outline: 'none',
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent)';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(29, 78, 216, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'var(--border)';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>

                            {/* Date & Time */}
                            <div className="form-group">
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.5rem',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: 'var(--text-secondary)',
                                }}>
                                    <Clock size={14} />
                                    Date & Time <span style={{ color: 'var(--error)' }}>*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    value={newEvent.scheduledAt}
                                    onChange={(e) => setNewEvent({ ...newEvent, scheduledAt: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.85rem 1rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border)',
                                        background: 'var(--surface-muted)',
                                        fontSize: '0.95rem',
                                        outline: 'none',
                                    }}
                                />
                            </div>

                            {/* Customer Dropdown - Searchable */}
                            <div className="form-group" ref={customerDropdownRef} style={{ position: 'relative' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.5rem',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: 'var(--text-secondary)',
                                }}>
                                    <Users size={14} />
                                    Link to Customer <span style={{ fontSize: '0.75rem', fontWeight: 400 }}>(optional)</span>
                                </label>

                                {/* Custom Searchable Select */}
                                <div
                                    onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                                    style={{
                                        width: '100%',
                                        padding: '0.85rem 1rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: showCustomerDropdown ? '2px solid var(--accent)' : '1px solid var(--border)',
                                        background: 'var(--surface-muted)',
                                        fontSize: '0.95rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <span style={{ color: newEvent.customerId ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                                        {newEvent.customerId
                                            ? customers.find(c => c._id === newEvent.customerId)?.name ||
                                            customers.find(c => c._id === newEvent.customerId)?.whatsappName ||
                                            customers.find(c => c._id === newEvent.customerId)?.phoneNumber ||
                                            'Selected Customer'
                                            : 'Search and select a customer...'}
                                    </span>
                                    <ChevronDown size={18} style={{ color: 'var(--text-secondary)', transform: showCustomerDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                </div>

                                {/* Dropdown Panel */}
                                {showCustomerDropdown && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        marginTop: '0.5rem',
                                        background: 'var(--surface)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius-md)',
                                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                                        zIndex: 100,
                                        overflow: 'hidden',
                                    }}>
                                        {/* Search Input */}
                                        <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                background: 'var(--surface-muted)',
                                                borderRadius: 'var(--radius-md)',
                                                padding: '0.5rem 0.75rem',
                                            }}>
                                                <Search size={16} style={{ color: 'var(--text-secondary)' }} />
                                                <input
                                                    type="text"
                                                    placeholder="Search by name or phone..."
                                                    value={customerSearch}
                                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        flex: 1,
                                                        border: 'none',
                                                        background: 'transparent',
                                                        outline: 'none',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    autoFocus
                                                />
                                            </div>
                                        </div>

                                        {/* Customer List */}
                                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {/* No Customer Option */}
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setNewEvent({ ...newEvent, customerId: undefined });
                                                    setShowCustomerDropdown(false);
                                                    setCustomerSearch('');
                                                }}
                                                style={{
                                                    padding: '0.75rem 1rem',
                                                    cursor: 'pointer',
                                                    background: !newEvent.customerId ? 'rgba(29, 78, 216, 0.08)' : 'transparent',
                                                    borderLeft: !newEvent.customerId ? '3px solid var(--accent)' : '3px solid transparent',
                                                    transition: 'all 0.15s ease',
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-muted)'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = !newEvent.customerId ? 'rgba(29, 78, 216, 0.08)' : 'transparent'}
                                            >
                                                <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No customer linked</span>
                                            </div>

                                            {filteredCustomers.length === 0 ? (
                                                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                                    No customers found
                                                </div>
                                            ) : (
                                                filteredCustomers.slice(0, 50).map((c) => (
                                                    <div
                                                        key={c._id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setNewEvent({ ...newEvent, customerId: c._id });
                                                            setShowCustomerDropdown(false);
                                                            setCustomerSearch('');
                                                        }}
                                                        style={{
                                                            padding: '0.75rem 1rem',
                                                            cursor: 'pointer',
                                                            background: newEvent.customerId === c._id ? 'rgba(29, 78, 216, 0.08)' : 'transparent',
                                                            borderLeft: newEvent.customerId === c._id ? '3px solid var(--accent)' : '3px solid transparent',
                                                            transition: 'all 0.15s ease',
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-muted)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = newEvent.customerId === c._id ? 'rgba(29, 78, 216, 0.08)' : 'transparent'}
                                                    >
                                                        <div style={{ fontWeight: 500 }}>{c.name || c.whatsappName || 'Unknown'}</div>
                                                        {c.phoneNumber && (
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                                                                {c.phoneNumber}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            <div className="form-group">
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: 'var(--text-secondary)',
                                }}>
                                    Notes <span style={{ fontSize: '0.75rem', fontWeight: 400 }}>(optional)</span>
                                </label>
                                <textarea
                                    value={newEvent.description || ''}
                                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                                    placeholder="Add any additional details..."
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '0.85rem 1rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border)',
                                        background: 'var(--surface-muted)',
                                        resize: 'vertical',
                                        fontSize: '0.95rem',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <footer style={{
                            padding: '1rem 1.5rem',
                            background: 'var(--surface-muted)',
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            gap: '0.75rem',
                            justifyContent: 'flex-end',
                        }}>
                            <button
                                className="ghost-button"
                                onClick={() => setShowModal(false)}
                                style={{ padding: '0.7rem 1.25rem' }}
                            >
                                Cancel
                            </button>
                            <button
                                className="button-primary"
                                onClick={handleCreateEvent}
                                style={{
                                    padding: '0.7rem 1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                <Plus size={16} />
                                Create Event
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};
